import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { appListSucursales, type SucursalPublic } from "../lib/api";
import { useAppSession } from "../hooks/useAppSession";
import { requestFullscreen } from "../hooks/useAppFullscreen";

export default function AppSetup() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAppSession();
  const [sucursales, setSucursales] = useState<SucursalPublic[]>([]);
  const [sucursalId, setSucursalId] = useState<number | null>(null);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Si ya hay sesión, redirect a landing.
  useEffect(() => {
    if (isAuthenticated) navigate("/", { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    appListSucursales()
      .then((rows) => {
        setSucursales(rows);
        if (rows.length === 1) setSucursalId(rows[0].id);
      })
      .catch((e) => setError(`No se pudo cargar sucursales: ${e.message}`));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!sucursalId || pin.length === 0) return;
    setError(null);
    setLoading(true);
    try {
      await login(sucursalId, pin);
      requestFullscreen(); // user gesture OK, podemos pedir fullscreen
      navigate("/", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setPin("");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl bg-[#1a1540] p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center gap-2">
          <Lock className="h-12 w-12 text-[#a78bfa]" />
          <h1 className="text-2xl font-bold">Activar pantalla</h1>
          <p className="text-center text-sm opacity-70">
            Seleccioná la sucursal y cargá el PIN configurado por el admin.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="opacity-80">Sucursal</span>
            <select
              value={sucursalId ?? ""}
              onChange={(e) =>
                setSucursalId(e.target.value ? parseInt(e.target.value, 10) : null)
              }
              className="rounded-lg bg-white/10 px-3 py-2 outline-none focus:ring-2 focus:ring-[#a78bfa]"
              required
            >
              <option value="">Elegí una sucursal…</option>
              {sucursales.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="opacity-80">PIN</span>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              className="rounded-lg bg-white/10 px-3 py-2 text-center text-2xl tracking-[0.4em] outline-none focus:ring-2 focus:ring-[#a78bfa]"
              placeholder="••••"
              required
            />
          </label>

          {error && (
            <div className="rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !sucursalId || pin.length === 0}
            className="flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-3 font-bold transition hover:scale-[1.02] active:scale-100 disabled:opacity-50"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Activar pantalla
          </button>
        </form>
      </div>
    </main>
  );
}
